import { useState } from 'react';
import { Select, Modal, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { ISpatStorageResource } from '../../entities/farms.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { getSpatSourceLabel } from '../../lib/farm.helpers';
import { sendSingleRequest } from '../../apis';
import CaretUpIcon from '../shared/icons/CaretUpIcon';
import CaretDownIcon from '../shared/icons/CaretDownIcon';
import SpatJourneyModal from '../view-modals/SpatJourneyModal';

type JourneyModalState = {
  spatId: number;
  data: any | null;
};

type Props = {
  batches: ISpatStorageResource[];
  className?: string;
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
};

const BatchSelection = ({
  batches,
  className,
  value,
  onChange,
  disabled,
}: Props) => {
  const lang = useSelector(selectLang);

  const [open, setOpen] = useState(false);
  const [journeyModal, setJourneyModal] = useState<JourneyModalState>();

  const handleInfoClick = async (e: React.MouseEvent, spatId: number) => {
    e.stopPropagation();
    setJourneyModal({ spatId, data: null });
    const res = await sendSingleRequest(
      {},
      'GET',
      `api/farm/spats-journey/${spatId}`,
      true,
    );
    if (res.status) {
      setJourneyModal({ spatId, data: res.data });
    } else {
      setJourneyModal(undefined);
    }
  };

  const handleSelectBatch = () => {
    if (journeyModal) {
      onChange?.(journeyModal.spatId);
      setJourneyModal(undefined);
    }
  };

  const filteredBatches = batches.filter(x => Number(x.available_weight) > 0);

  return (
    <div className={className}>
      <label className='dropdown__label'>{translate(lang, 'Spat')}</label>
      <Select
        size='large'
        open={open}
        disabled={disabled}
        placeholder={translate(lang, 'Spat')}
        value={value != null ? value.toString() : undefined}
        onDropdownVisibleChange={o => setOpen(o)}
        onChange={val => onChange?.(Number(val) || null)}
        showSearch
        optionLabelProp='label'
        filterOption={(input, option) =>
          ((option?.label as string) ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        suffixIcon={
          open ? (
            <div className='dropdown__icon dropdown__icon--rotate-180'>
              <CaretDownIcon />
            </div>
          ) : (
            <div className='dropdown__icon dropdown__icon--rotate-0'>
              <CaretUpIcon />
            </div>
          )
        }
        style={{ width: '100%' }}
      >
        {filteredBatches.map(item => {
          const label = getSpatSourceLabel(item.id, batches) ?? '';
          return (
            <Select.Option
              key={item.id}
              value={item.id.toString()}
              label={label}
            >
              <div className='batch-option'>
                <span className='batch-option__label'>{label}</span>
                <span
                  role='button'
                  tabIndex={0}
                  className='batch-option__info-btn'
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => handleInfoClick(e, item.id)}
                >
                  <InfoCircleOutlined />
                </span>
              </div>
            </Select.Option>
          );
        })}
      </Select>

      {journeyModal?.data != null ? (
        <SpatJourneyModal
          data={journeyModal.data}
          visible={true}
          onClose={() => setJourneyModal(undefined)}
          onSelect={handleSelectBatch}
        />
      ) : (
        !!journeyModal && (
          <Modal visible={true} footer={null} closable={false} width={200}>
            <div className='d-flex justify-center'>
              <Spin />
            </div>
          </Modal>
        )
      )}
    </div>
  );
};

export default BatchSelection;
